/**
 * Document Generation Hooks (DRR-232 Track E)
 *
 * Provides typed hooks for generating various PDF documents.
 * All mutations create Document primitives and return S3 keys for download.
 */

import {
  useGenerateAdmissionPdfMutation,
  type GenerateAdmissionPdfInput,
} from '@/generated/graphql';

/**
 * Hook for generating admission documents (MN 144G).
 *
 * Usage:
 * ```tsx
 * const { generateAdmissionPdf, loading } = useAdmissionPdfGeneration();
 *
 * const result = await generateAdmissionPdf({
 *   tenantId: '...',
 *   templateId: 'pdf-tpl-hc-alf-admission-agreement-001',
 *   documentName: 'Admission Agreement - John Doe - 2026-04-11',
 *   dataContextJson: JSON.stringify({ residentName: 'John Doe', ... }),
 *   workflowInstanceId: 'inst-...',
 *   regulatoryRuleIds: ['rl-4-0-009', 'rl-4-0-012'],
 * });
 * ```
 */
export function useAdmissionPdfGeneration() {
  const [generatePdf, { loading, error }] = useGenerateAdmissionPdfMutation();

  const generateAdmissionPdf = async (input: GenerateAdmissionPdfInput) => {
    const result = await generatePdf({ variables: { input } });
    return result.data?.generateAdmissionPdf ?? null;
  };

  return {
    generateAdmissionPdf,
    loading,
    error,
  };
}
